import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { LearnerProfile_EducationHistoryMajor as EducationHistoryMajor } from '__generated__/graphql-types';
import type { LearnerProfile_OccupationPreferenceLevel as PreferenceLevel } from '__generated__/graphql-types';
import type {
  LearnerProfile_EnumGenderEntry,
  LearnerProfile_LearnerProfileGender,
  LearnerProfile_UserGenderEntry,
  LearnerProfile_WorkHistory as WorkHistory,
} from '__generated__/graphql-types-do-not-use';
import type { ApolloError } from 'apollo-client';
import gql from 'graphql-tag';

import user from 'js/lib/user';

import type { LearnerProfile } from 'bundles/account-profile/common/types';
import type {
  DemographicscurrentOccupationOccupationId,
  DemographicscurrentOccupationUserGeneratedOccupation,
  EducationMajor,
  StandardProfile,
} from 'bundles/naptimejs/resources/__generated__/ProfilesV1';

type ProfilesV1UpdateMutationVariables = {
  id: string;
  input: Partial<Omit<StandardProfile, 'individualPrivacySettings'>> & {
    individualPrivacySettings: { profilePrivacy: number; recruitingProfilePrivacy: number };
  };
  validateRecruitingPipeline?: boolean;
};

export type UpdateLearnerProfileMutationResponse = { elements: StandardProfile[]; error?: ApolloError };

export const updateProfilesV1 = gql`
  mutation UpdateLearnerProfilesV1Mutation($id: String!, $input: StandardProfile) {
    ProfilesV1(id: $id, input: $input) @rest(type: ProfilesV1Resource, path: "profiles.v1/{args.id}", method: "PUT") {
      update(id: $id, input: $input) {
        id
        userId: id
        externalId
        individualPrivacySettings {
          profilePrivacy
          recruitingProfilePrivacy
        }
        demographics {
          interestedCareers
          openToRemoteWork
          willingToRelocate
          workHistory
        }
        gender
        bio
        fullName
        location {
          country
          city
        }
        photos
        privacy
        pronoun
        resumeUrl
        websites
        countryWorkEligibilities
      }
    }
  }
`;

const prefLevelToEnum: { [name in PreferenceLevel]: number } = {
  NOT_APPLICABLE: 0,
  // @ts-expect-error The IC role was added to the schema, but the roles have not been migrated from scala yet. Currently the IC role does not exist in the BE
  INDIVIDUAL_CONTRIBUTOR: 0,
  INTERN: 1,
  JUNIOR_INDIVIDUAL_CONTRIBUTOR: 2,
  SENIOR_INDIVIDUAL_CONTRIBUTOR: 3,
  MANAGERIAL_LEADERSHIP_ROLE: 4,
  DIRECTOR: 5,
  EXECUTIVE_MANAGEMENT: 6,
};

const PrivacySetting = {
  ONLY_ME: 10,
  EVERYONE: 1000,
};

const formatOccupation = (occupation: WorkHistory) => {
  if (!occupation.jobTitle) {
    return null;
  } else if ('id' in occupation.jobTitle) {
    return {
      typeName: 'OccupationId' as const,
      definition: { id: occupation.jobTitle.id },
    } as DemographicscurrentOccupationOccupationId;
  } else {
    return {
      typeName: 'UserGeneratedOccupation' as const,
      definition: { name: occupation.jobTitle.name },
    } as DemographicscurrentOccupationUserGeneratedOccupation;
  }
};

/**
 *
 * This function maps the state for the learner profile gender into a format the api expects on update
 */
/* eslint-disable camelcase */
const getLearnerGender = (learnerProfileGender?: LearnerProfile_LearnerProfileGender | null) => {
  const userGenderInput = (learnerProfileGender as LearnerProfile_UserGenderEntry)?.userGenderInput;
  // We accept empty string as a valid input
  if (userGenderInput || userGenderInput === '') {
    return {
      typeName: 'LearnerProfileGenderUserEntry',
      definition: {
        userGenderInput: (learnerProfileGender as LearnerProfile_UserGenderEntry).userGenderInput,
      },
    };
  }

  if ((learnerProfileGender as LearnerProfile_EnumGenderEntry)?.selectedEnum) {
    return {
      typeName: 'LearnerProfileGenderEnumEntry',
      definition: {
        selectedEnum: (learnerProfileGender as LearnerProfile_EnumGenderEntry).selectedEnum,
      },
    };
  }

  return null;
};

export const formatLearnerProfileMutation = (learnerProfileState: Partial<LearnerProfile>) => {
  const {
    externalId,
    about,
    fullName,
    includedInTalentPipeline,
    willingToRelocate,
    openToRemoteWork,
    showToPublic,
    profilePhoto,
    pronoun,
    externalLinks,
    resumeUrl,
    occupationPreferences,
    location,
    workHistory = [],
    educationHistory = [],
  } = learnerProfileState;

  const interestedCareers = occupationPreferences?.map((pref) => {
    let industry;
    if ('industry' in pref && pref.industry) {
      industry =
        'id' in pref.industry
          ? { typeName: 'IndustryId' as const, definition: { id: pref.industry.id } }
          : { typeName: 'UserGeneratedIndustry' as const, definition: { name: pref.industry.name } };
    }

    const formattedCareer = {
      occupationLevel: pref.level ? prefLevelToEnum[pref.level] ?? null : null,
      occupation: {
        ...('id' in pref.occupation
          ? { typeName: 'OccupationId' as const, definition: { id: pref.occupation.id } }
          : { typeName: 'UserGeneratedOccupation' as const, definition: { name: pref.occupation.name } }),
      },
      industry,
    };

    return formattedCareer;
  });

  return {
    externalId: externalId ?? '',
    userId: user.get().id,
    bio: about,
    fullName,
    pronoun,
    resumeUrl,
    // The learnerProfileGender profiles.v1 field doesn't allow for the deletion of gender so the legacy gender field is being used as an interim solution to delete
    // gender until we migrate the profiles.v1 naptime mutation to the migrated graphql learner profile mutations
    // Ticket for profiles.v1 to graphql migration FE SPIKE - https://coursera.atlassian.net/browse/LPD-644
    ...('learnerProfileGender' in learnerProfileState && learnerProfileState?.learnerProfileGender
      ? { learnerProfileGender: getLearnerGender(learnerProfileState?.learnerProfileGender) }
      : { gender: -1 }),
    location: {
      state: location?.stateName,
      // Due to an issue where the DB column that stores the location country name incorrectly trunacating values
      // (ex. 'United States' being returned as 'United Sta') we have decided to use the country code instead to have stricter
      // country name validation. The naptime profiles.v1 mutation has been changed to only accept country codes in the country field which
      // is why country code is being mappaed to the country field here. Once we migrate it to a graphql mutation, we will be able to input the
      // country code in a countryCode field - https://coursera.atlassian.net/browse/LPD-644
      country: (location && 'countryCode' in location && location.countryCode) || undefined,
      city: location?.cityName,
      lat: location?.latitude,
      lng: location?.longitude,
    },
    individualPrivacySettings: {
      recruitingProfilePrivacy: includedInTalentPipeline ? PrivacySetting.EVERYONE : PrivacySetting.ONLY_ME,
      profilePrivacy: showToPublic ? PrivacySetting.EVERYONE : PrivacySetting.ONLY_ME,
    },
    demographics: {
      educationHistory: educationHistory?.map((ed) => ({
        major: ed.major
          ? (Object.entries(EducationHistoryMajor).find((entry) => entry[1] === ed.major)?.[0] as EducationMajor)
          : null,
        degreeType: ed.degreeType,
        cgpaX100: ed.gradePointAverageX100,
        grade: ed.grade,
        isCurrentlyAStudent: Boolean(ed.isCurrentlyAStudent),
        instituitionName: ed.institutionName,
        startDate: ed.startDate,
        graduationDate: ed.graduationDate,
        areaOfConcentration: ed.areaOfConcentration,
      })),
      willingToRelocate,
      openToRemoteWork,
      interestedCareers,
      workHistory: workHistory?.map((work: WorkHistory) => ({
        employerName: work.employerName,
        isCurrentlyEmployed: Boolean(work.isCurrentlyEmployed),
        description: work.description ?? '',
        startDate: work.startDate,
        endDate: work.endDate,
        occupation: formatOccupation(work),
      })),
    },
    ...(profilePhoto
      ? {
          photos: {
            ...profilePhoto,
          },
        }
      : {}),
    websites: externalLinks,
  };
};

export const useUpdateLearnerProfile = () => {
  const [generateUpdateLearnerProfileMutation] = useMutation<
    UpdateLearnerProfileMutationResponse,
    ProfilesV1UpdateMutationVariables
  >(updateProfilesV1);

  const updateLearnerProfileMutation = async (learnerProfileState: Partial<LearnerProfile>) => {
    try {
      const formattedLearnerProfileState = formatLearnerProfileMutation(learnerProfileState);
      const result = await generateUpdateLearnerProfileMutation({
        variables: {
          id: user.get()?.id?.toString(),
          input: formattedLearnerProfileState,
        },
      });

      return result;
    } catch (error) {
      Sentry.captureException(error);
      return Promise.reject(error);
    }
  };

  return { updateLearnerProfileMutation };
};
